import React, { useState, useEffect, useCallback } from 'react';
import './SecretsModal.css';
import { modalInputClass } from './modalInputClass';
import { useModalSelectionGuard, preventModalOverlaySelection } from '../hooks/useModalSelectionGuard';

const isDefined = (value) => value !== null && value !== undefined;

const formatDefaultPercent = (value) =>
  isDefined(value) && !isNaN(Number(value)) ? Number(value).toFixed(2) : '';

const AlertModal = ({
  isOpen,
  onClose,
  onCreateAlert,
  stock,
  futures,
  defaultStockPrice,
  defaultFuturesPrice,
  defaultDivPercent,
  defaultExpPercent,
  defaultSpread,
  selectedBroker
}) => {
  useModalSelectionGuard(isOpen);

  const [stockPrice, setStockPrice] = useState(() =>
    isDefined(defaultStockPrice) ? String(defaultStockPrice) : ''
  );
  const [futuresPrice, setFuturesPrice] = useState(() =>
    isDefined(defaultFuturesPrice) ? String(defaultFuturesPrice) : ''
  );
  const [divPercent, setDivPercent] = useState(() => formatDefaultPercent(defaultDivPercent));
  const [expPercent, setExpPercent] = useState(() => formatDefaultPercent(defaultExpPercent));
  const [spread, setSpread] = useState(() => formatDefaultPercent(defaultSpread));
  const [broker, setBroker] = useState(selectedBroker);
  const [error, setError] = useState(null);

  const handleStockPriceChange = (e) => {
    const value = e.target.value;
    if (!/^-?[\d.,]*$/.test(value)) return;
    setStockPrice(value);
    if (error) setError(null);
  };

  const handleFuturesPriceChange = (e) => {
    const value = e.target.value;
    if (!/^-?[\d.,]*$/.test(value)) return;
    setFuturesPrice(value);
    if (error) setError(null);
  };

  const handleDivPercentChange = (e) => {
    const value = e.target.value;
    if (!/^-?[\d.,]*$/.test(value)) return;
    setDivPercent(value);
    if (error) setError(null);
  };

  const handleExpPercentChange = (e) => {
    const value = e.target.value;
    if (!/^-?[\d.,]*$/.test(value)) return;
    setExpPercent(value);
    if (error) setError(null);
  };

  const handleSpreadChange = (e) => {
    const value = e.target.value;
    if (!/^-?[\d.,]*$/.test(value)) return;
    setSpread(value);
    if (error) setError(null);
  };

  const handleCancel = useCallback(() => {
    setStockPrice('');
    setFuturesPrice('');
    setDivPercent('');
    setExpPercent('');
    setSpread('');
    setBroker(selectedBroker);
    setError(null);
    onClose();
  }, [onClose, selectedBroker]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleCancel]);

  const handleOk = () => {
    // Проверяем, что хотя бы одно поле заполнено
    const stockPriceValue = stockPrice.trim() ? parseFloat(stockPrice.replace(',', '.')) : null;
    const futuresPriceValue = futuresPrice.trim() ? parseFloat(futuresPrice.replace(',', '.')) : null;
    const divValue = divPercent.trim() ? parseFloat(divPercent.replace(',', '.')) : null;
    const expValue = expPercent.trim() ? parseFloat(expPercent.replace(',', '.')) : null;
    const spreadValue = spread.trim() ? parseFloat(spread.replace(',', '.')) : null;

    if (
      stockPriceValue === null &&
      futuresPriceValue === null &&
      divValue === null &&
      expValue === null &&
      spreadValue === null
    ) {
      setError('Заполните хотя бы одно поле');
      return;
    }

    // Проверяем, что значения являются числами
    if (stockPrice.trim() && isNaN(stockPriceValue)) {
      setError('Цена акции должна быть числом');
      return;
    }

    if (futuresPrice.trim() && isNaN(futuresPriceValue)) {
      setError('Цена фьючерса должна быть числом');
      return;
    }

    if (divPercent.trim() && isNaN(divValue)) {
      setError('Процент к дивидендной отсечке должен быть числом');
      return;
    }

    if (expPercent.trim() && isNaN(expValue)) {
      setError('Процент к экспирации должен быть числом');
      return;
    }

    if (spread.trim() && isNaN(spreadValue)) {
      setError('Спред должен быть числом');
      return;
    }

    const roundToTwoDecimals = (value) => {
      if (value === null || value === undefined) return null;
      return Number(value.toFixed(2));
    };

    onCreateAlert(
      roundToTwoDecimals(divValue),
      roundToTwoDecimals(expValue),
      roundToTwoDecimals(spreadValue),
      stockPriceValue,
      futuresPriceValue,
      broker
    );
  };

  if (!isOpen) return null;

  const defaultDivPercentValue =
    isDefined(defaultDivPercent) && !isNaN(defaultDivPercent)
      ? Number(defaultDivPercent).toFixed(2)
      : '';
  const defaultExpPercentValue =
    isDefined(defaultExpPercent) && !isNaN(defaultExpPercent)
      ? Number(defaultExpPercent).toFixed(2)
      : '';
  const defaultSpreadValue =
    isDefined(defaultSpread) && !isNaN(defaultSpread)
      ? Number(defaultSpread).toFixed(2)
      : '';
  const defaultStockPriceValue =
    isDefined(defaultStockPrice) && !isNaN(defaultStockPrice)
      ? String(defaultStockPrice)
      : '';
  const defaultFuturesPriceValue =
    isDefined(defaultFuturesPrice) && !isNaN(defaultFuturesPrice)
      ? String(defaultFuturesPrice)
      : '';

  return (
    <div className="secrets-modal-overlay" onSelectStart={preventModalOverlaySelection}>
      <div className="secrets-modal" onClick={e => e.stopPropagation()}>
        <div className="secrets-modal-header">
          <h2>Создать уведомление</h2>
          <button type="button" className="secrets-close-btn" onClick={handleCancel} aria-label="Закрыть">×</button>
        </div>

        <div className="secrets-form">
          <div className="form-group">
            <div className="secrets-modal-summary">
              <strong>{stock} / {futures}{' // '}{broker === 'bcs' ? 'БКС' : 'Финам'}</strong>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="stockPrice">Превышение цены акции</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="stockPrice"
                value={stockPrice}
                onChange={handleStockPriceChange}
                placeholder="Цена"
                inputMode="decimal"
                className={modalInputClass({ withActions: true })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setStockPrice(defaultStockPriceValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить цену акции по умолчанию"
                  disabled={stockPrice === defaultStockPriceValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStockPrice('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить цену акции"
                  disabled={!stockPrice}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="futuresPrice">Понижение цены фьючерса</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="futuresPrice"
                value={futuresPrice}
                onChange={handleFuturesPriceChange}
                placeholder="Цена"
                inputMode="decimal"
                className={modalInputClass({ withActions: true })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setFuturesPrice(defaultFuturesPriceValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить цену фьючерса по умолчанию"
                  disabled={futuresPrice === defaultFuturesPriceValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFuturesPrice('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить цену фьючерса"
                  disabled={!futuresPrice}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="spread">Превышение спреда</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="spread"
                value={spread}
                onChange={handleSpreadChange}
                placeholder="%"
                inputMode="decimal"
                className={modalInputClass({ withActions: true })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setSpread(defaultSpreadValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить спред по умолчанию"
                  disabled={spread === defaultSpreadValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSpread('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить спред"
                  disabled={!spread}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="divPercent">Превышение %% к ДО</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="divPercent"
                value={divPercent}
                onChange={handleDivPercentChange}
                placeholder="%"
                inputMode="decimal"
                className={modalInputClass({ withActions: true })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setDivPercent(defaultDivPercentValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить процент к дивидендной отсечке по умолчанию"
                  disabled={divPercent === defaultDivPercentValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDivPercent('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить процент к дивидендной отсечке"
                  disabled={!divPercent}
                >
                  ×
                </button>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="expPercent">Превышение %% к экспирации</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="expPercent"
                value={expPercent}
                onChange={handleExpPercentChange}
                placeholder="%"
                inputMode="decimal"
                className={modalInputClass({ withActions: true })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setExpPercent(defaultExpPercentValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить процент к экспирации по умолчанию"
                  disabled={expPercent === defaultExpPercentValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setExpPercent('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить процент к экспирации"
                  disabled={!expPercent}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="secrets-modal-buttons">
            <button type="button" className="secrets-cancel-btn" onClick={handleCancel}>
              Отмена
            </button>
            <button type="button" className="secrets-save-btn" onClick={handleOk}>
              Создать
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertModal;
